import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Lock, Download, TrendingUp } from "lucide-react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Line, ComposedChart } from "recharts";

export function AnalyticsPanel({ onUpgrade }) {
  const { t } = useTranslation();
  const [locked, setLocked] = useState(false);
  const [fleet, setFleet] = useState([]);
  const [forecast, setForecast] = useState(null);

  useEffect(() => {
    api.get("/loueur/analytics/fleet").then((r) => setFleet(r.data.fleet)).catch((e) => { if (e.response?.status === 403) setLocked(true); });
    api.get("/loueur/analytics/forecast").then((r) => setForecast(r.data)).catch(() => {});
  }, []);

  const download = (format) => {
    fetch(`${API}/loueur/analytics/export?format=${format}`, { credentials: "include" })
      .then((r) => r.blob()).then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `caribbean-drive-compta.${format === "xlsx" ? "xlsx" : "csv"}`;
        a.click(); URL.revokeObjectURL(url);
      });
  };

  if (locked) return (
    <div data-testid="analytics-locked" className="text-center py-12 bg-white rounded-2xl border border-border">
      <Lock className="w-8 h-8 text-canard mx-auto mb-3" />
      <p className="text-muted2 font-body mb-4">{t("analytics.locked")}</p>
      <Button data-testid="analytics-upgrade" onClick={onUpgrade} className="rounded-full bg-canard hover:bg-canard-dark font-bold">{t("litiges.upgrade")}</Button>
    </div>
  );

  const chartData = forecast ? [...forecast.history.map((h) => ({ ...h, kind: "hist" })), ...forecast.forecast.map((f) => ({ ...f, forecast: f.net, net: null }))] : [];

  return (
    <div data-testid="analytics-panel" className="space-y-4">
      <div className="flex flex-wrap gap-2 justify-end">
        <Button data-testid="export-csv" onClick={() => download("csv")} variant="outline" className="rounded-full font-bold"><Download className="w-4 h-4 mr-1" /> {t("analytics.exportCsv")}</Button>
        <Button data-testid="export-xlsx" onClick={() => download("xlsx")} variant="outline" className="rounded-full font-bold"><Download className="w-4 h-4 mr-1" /> {t("analytics.exportXlsx")}</Button>
      </div>

      <div className="bg-white rounded-2xl shadow-card border border-border p-5">
        <p className="font-heading font-bold text-ink flex items-center gap-2 mb-4"><TrendingUp className="w-5 h-5 text-canard" /> {t("analytics.forecast")}</p>
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={chartData} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E8EFEF" vertical={false} />
            <XAxis dataKey="month" tickFormatter={(m) => m.slice(5) + "/" + m.slice(2, 4)} tick={{ fontSize: 11, fill: "#4B6B6B" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#4B6B6B" }} axisLine={false} tickLine={false} />
            <Tooltip formatter={(v) => [`${v} €`]} contentStyle={{ borderRadius: 12, border: "1px solid #E8EFEF", fontSize: 12 }} />
            <Bar dataKey="net" name={t("analytics.actual")} fill="#2D7E7E" radius={[6, 6, 0, 0]} maxBarSize={26} />
            <Line type="monotone" dataKey="forecast" name={t("analytics.predicted")} stroke="#3EB489" strokeWidth={2.5} strokeDasharray="5 4" dot={{ r: 3, fill: "#3EB489" }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white rounded-2xl shadow-card border border-border overflow-x-auto">
        <p className="font-heading font-bold text-ink p-4 pb-0">{t("analytics.fleetTitle")}</p>
        <table className="w-full text-sm mt-3">
          <thead className="bg-sand"><tr className="text-left text-muted2 font-bold text-xs uppercase"><th className="p-3">{t("litiges.vehicle")}</th><th className="p-3">{t("dash.bookings")}</th><th className="p-3">{t("analytics.net")}</th><th className="p-3">{t("stats.occupancy")}</th><th className="p-3">{t("analytics.rating")}</th></tr></thead>
          <tbody>
            {fleet.map((f) => (
              <tr key={f.vehicle_id} data-testid={`fleet-row-${f.vehicle_id}`} className="border-t border-border font-body">
                <td className="p-3 font-semibold text-ink">{f.name}</td>
                <td className="p-3">{f.bookings}</td>
                <td className="p-3 font-bold">{f.net_revenue} €</td>
                <td className="p-3">{f.occupancy}%</td>
                <td className="p-3">{f.rating || "—"}</td>
              </tr>
            ))}
            {!fleet.length && <tr><td colSpan={5} className="p-8 text-center text-muted2">{t("stats.noData")}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
